import { v4 as uuid } from 'uuid'
import type { Note, ClassItem, Resource } from '@/types'
import { storage } from './storage'

export function seedIfEmpty() {
  if (storage.get('seeded')) return

  const notes: Note[] = [
    {
      id: uuid(),
      title: 'Photosynthesis – Key Points',
      content:
        '## Overview\nPhotosynthesis is the process by which green plants make food using sunlight.\n\n**Equation:** 6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂\n\n### Key Points\n- Occurs in chloroplasts\n- Two stages: Light reactions & Calvin cycle\n- Chlorophyll absorbs red and blue light\n\n### Important for Exam\n- Difference between C3, C4 and CAM plants\n- Role of stomata',
      subject: 'Biology',
      grade: '10',
      chapter: 'Life Processes',
      tags: ['photosynthesis', 'plants', 'exam-important'],
      isPinned: true,
      color: 'green',
      createdAt: new Date(Date.now() - 86400000 * 3).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
    },
    {
      id: uuid(),
      title: 'Quadratic Equations – Formula Sheet',
      content:
        '## Quadratic Formula\n\n**Standard form:** ax² + bx + c = 0\n\n**Formula:** x = (-b ± √(b²-4ac)) / 2a\n\n### Discriminant (D = b²-4ac)\n- D > 0: Two distinct real roots\n- D = 0: Two equal real roots  \n- D < 0: No real roots\n\n### Sum & Product of Roots\n- Sum = -b/a\n- Product = c/a',
      subject: 'Mathematics',
      grade: '10',
      chapter: 'Quadratic Equations',
      tags: ['algebra', 'formulas', 'class-10'],
      isPinned: true,
      color: 'blue',
      createdAt: new Date(Date.now() - 86400000 * 5).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    },
    {
      id: uuid(),
      title: 'The French Revolution – Timeline',
      content:
        '## Key Events\n\n| Year | Event |\n|------|-------|\n| 1789 | Estates-General meets, Bastille stormed |\n| 1791 | Constitutional Monarchy established |\n| 1792 | France declared a Republic |\n| 1793 | Reign of Terror begins |\n| 1799 | Napoleon seizes power |\n\n### Causes\n1. Financial crisis in France\n2. Social inequality (Three Estates)\n3. Enlightenment ideas\n4. Influence of American Revolution',
      subject: 'History',
      grade: '9',
      chapter: 'The French Revolution',
      tags: ['history', 'revolution', 'europe'],
      isPinned: false,
      color: 'yellow',
      createdAt: new Date(Date.now() - 86400000 * 7).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 6).toISOString(),
    },
    {
      id: uuid(),
      title: 'Periodic Table – Important Groups',
      content:
        '## Group 1 – Alkali Metals\nLi, Na, K, Rb, Cs, Fr\n- Highly reactive\n- React with water to form hydroxides\n\n## Group 17 – Halogens\nF, Cl, Br, I, At\n- Non-metals\n- Form salts with metals\n\n## Group 18 – Noble Gases\nHe, Ne, Ar, Kr, Xe, Rn\n- Chemically inert\n- Used in lighting',
      subject: 'Chemistry',
      grade: '11',
      chapter: 'Periodic Classification',
      tags: ['periodic-table', 'elements', 'groups'],
      isPinned: false,
      color: 'purple',
      createdAt: new Date(Date.now() - 86400000 * 10).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 10).toISOString(),
    },
  ]

  const classes: ClassItem[] = [
    {
      id: uuid(),
      name: 'Class 10A',
      subject: 'Mathematics',
      grade: 10,
      section: 'A',
      studentCount: 42,
      schedule: [
        { day: 'Monday', startTime: '09:00', endTime: '09:45', room: 'Room 201' },
        { day: 'Wednesday', startTime: '10:00', endTime: '10:45', room: 'Room 201' },
        { day: 'Friday', startTime: '11:00', endTime: '11:45', room: 'Room 201' },
      ],
      description: 'Grade 10 Mathematics – Algebra, Geometry, Trigonometry',
      academicYear: '2024-25',
      color: '#3b82f6',
      createdAt: new Date(Date.now() - 86400000 * 90).toISOString(),
    },
    {
      id: uuid(),
      name: 'Class 9B',
      subject: 'Science',
      grade: 9,
      section: 'B',
      studentCount: 38,
      schedule: [
        { day: 'Tuesday', startTime: '08:00', endTime: '08:45', room: 'Lab 1' },
        { day: 'Thursday', startTime: '09:00', endTime: '09:45', room: 'Lab 1' },
        { day: 'Saturday', startTime: '10:00', endTime: '10:45', room: 'Room 105' },
      ],
      description: 'Grade 9 Science – Physics, Chemistry, Biology basics',
      academicYear: '2024-25',
      color: '#10b981',
      createdAt: new Date(Date.now() - 86400000 * 85).toISOString(),
    },
    {
      id: uuid(),
      name: 'Class 11A',
      subject: 'Physics',
      grade: 11,
      section: 'A',
      studentCount: 35,
      schedule: [
        { day: 'Monday', startTime: '11:00', endTime: '11:45', room: 'Physics Lab' },
        { day: 'Wednesday', startTime: '14:00', endTime: '14:45', room: 'Physics Lab' },
        { day: 'Friday', startTime: '09:00', endTime: '09:45', room: 'Room 301' },
      ],
      description: 'Grade 11 Physics – Mechanics, Thermodynamics, Waves',
      academicYear: '2024-25',
      color: '#8b5cf6',
      createdAt: new Date(Date.now() - 86400000 * 80).toISOString(),
    },
    {
      id: uuid(),
      name: 'Class 8C',
      subject: 'English',
      grade: 8,
      section: 'C',
      studentCount: 45,
      schedule: [
        { day: 'Tuesday', startTime: '10:00', endTime: '10:45', room: 'Room 102' },
        { day: 'Thursday', startTime: '11:00', endTime: '11:45', room: 'Room 102' },
      ],
      description: 'Grade 8 English – Literature, Grammar, Writing',
      academicYear: '2024-25',
      color: '#f59e0b',
      createdAt: new Date(Date.now() - 86400000 * 75).toISOString(),
    },
  ]

  const resources: Resource[] = [
    {
      id: uuid(),
      title: 'NCERT Mathematics Class 10 – Full PDF',
      description: 'Complete NCERT textbook for Class 10 Mathematics covering all chapters',
      type: 'pdf',
      url: 'https://ncert.nic.in/textbook/pdf/jemh1dd.zip',
      subject: 'Mathematics',
      grade: [10],
      chapter: 'All Chapters',
      tags: ['ncert', 'textbook', 'class-10'],
      isBookmarked: true,
      fileSize: '12.4 MB',
      uploadedAt: new Date(Date.now() - 86400000 * 30).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 30).toISOString(),
    },
    {
      id: uuid(),
      title: 'Photosynthesis Animation – Khan Academy',
      description: 'Interactive video explaining the light and dark reactions of photosynthesis',
      type: 'video',
      url: 'https://www.khanacademy.org/science/ap-biology/cellular-energetics/photosynthesis-ap/v/photosynthesis',
      subject: 'Biology',
      grade: [9, 10, 11],
      chapter: 'Life Processes',
      tags: ['video', 'animation', 'photosynthesis'],
      isBookmarked: true,
      uploadedAt: new Date(Date.now() - 86400000 * 20).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 20).toISOString(),
    },
    {
      id: uuid(),
      title: 'Periodic Table Interactive',
      description: 'Interactive periodic table with element details, electron configuration, and properties',
      type: 'link',
      url: 'https://ptable.com',
      subject: 'Chemistry',
      grade: [9, 10, 11, 12],
      chapter: 'Periodic Classification',
      tags: ['interactive', 'elements', 'reference'],
      isBookmarked: false,
      uploadedAt: new Date(Date.now() - 86400000 * 15).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 15).toISOString(),
    },
    {
      id: uuid(),
      title: 'French Revolution – Worksheet',
      description: 'Printable worksheet with MCQs, short answers, and map work for Class 9 History',
      type: 'document',
      url: '#',
      subject: 'History',
      grade: [9],
      chapter: 'The French Revolution',
      tags: ['worksheet', 'exam-prep', 'class-9'],
      isBookmarked: false,
      fileSize: '245 KB',
      uploadedAt: new Date(Date.now() - 86400000 * 10).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 10).toISOString(),
    },
    {
      id: uuid(),
      title: 'Algebra Basics – Presentation',
      description: 'PowerPoint slides covering algebraic expressions, identities, and factorization',
      type: 'presentation',
      url: '#',
      subject: 'Mathematics',
      grade: [8, 9],
      chapter: 'Algebra',
      tags: ['slides', 'algebra', 'classroom'],
      isBookmarked: false,
      fileSize: '3.2 MB',
      uploadedAt: new Date(Date.now() - 86400000 * 8).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 8).toISOString(),
    },
    {
      id: uuid(),
      title: 'Newton\'s Laws – Lab Manual',
      description: 'Practical experiments for demonstrating Newton\'s three laws of motion',
      type: 'pdf',
      url: '#',
      subject: 'Physics',
      grade: [9, 10, 11],
      chapter: 'Laws of Motion',
      tags: ['practical', 'lab', 'newton'],
      isBookmarked: true,
      fileSize: '1.8 MB',
      uploadedAt: new Date(Date.now() - 86400000 * 5).toISOString(),
      updatedAt: new Date(Date.now() - 86400000 * 5).toISOString(),
    },
  ]

  storage.set('notes', notes)
  storage.set('classes', classes)
  storage.set('resources', resources)
  storage.set('seeded', true)
}
